(() => {
  const cleanups = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initReveal(root, abort) {
    const title = root.querySelector('[data-mrw-title]');
    const reveal = Array.from(root.querySelectorAll('[data-mrw-reveal]'));
    const stagger = Array.from(root.querySelectorAll('[data-mrw-stagger]')).flatMap((group) => Array.from(group.children));
    const targets = [title, ...reveal, ...stagger].filter(Boolean);
    if (!targets.length) return () => {};

    if (!reduceMotion.matches && window.gsap && window.ScrollTrigger && window.innerWidth >= 750) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      const ctx = window.gsap.context(() => {
        if (title) {
          window.gsap.fromTo(title, { autoAlpha: 0, y: 70 }, { autoAlpha: 1, y: 0, duration: 1, ease: 'expo.out', scrollTrigger: { trigger: root, start: 'top 80%' } });
        }
        reveal.forEach((element) => {
          window.gsap.fromTo(element, { autoAlpha: 0, y: 44 }, { autoAlpha: 1, y: 0, duration: .85, ease: 'power3.out', scrollTrigger: { trigger: element, start: 'top 86%' } });
        });
        const groups = root.querySelectorAll('[data-mrw-stagger]');
        groups.forEach((group) => {
          window.gsap.fromTo(Array.from(group.children), { autoAlpha: 0, y: 48 }, { autoAlpha: 1, y: 0, duration: .8, stagger: .11, ease: 'power3.out', scrollTrigger: { trigger: group, start: 'top 84%' } });
        });
        if (root.matches('[data-mrw-section="hero"]')) {
          const image = root.querySelector('.mrw-hero__image');
          if (image) window.gsap.to(image, { yPercent: 12, ease: 'none', scrollTrigger: { trigger: root, start: 'top top', end: 'bottom top', scrub: true } });
        }
      }, root);
      return () => ctx.revert();
    }

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-mrw-visible'));
      return () => {};
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-mrw-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: .08 });
    targets.forEach((target) => observer.observe(target));
    abort.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    return () => observer.disconnect();
  }

  function initTabs(root, abort) {
    if (!root.matches('[data-mrw-tabs]')) return;
    const tabs = Array.from(root.querySelectorAll('[data-mrw-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-mrw-panel]'));
    const activate = (index, focus = false) => {
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index), { signal: abort.signal });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        activate(next, true);
      }, { signal: abort.signal });
    });
  }

  function initAccordion(root, abort) {
    if (!root.matches('[data-mrw-accordion]')) return;
    const buttons = Array.from(root.querySelectorAll('[data-mrw-accordion-button]'));
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const wasOpen = button.getAttribute('aria-expanded') === 'true';
        buttons.forEach((other) => {
          other.setAttribute('aria-expanded', 'false');
          const panel = root.querySelector(`#${CSS.escape(other.getAttribute('aria-controls'))}`);
          if (panel) panel.hidden = true;
        });
        if (!wasOpen) {
          button.setAttribute('aria-expanded', 'true');
          const panel = root.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
          if (panel) panel.hidden = false;
        }
      }, { signal: abort.signal });
    });
  }

  function initVideo(root, abort) {
    if (!root.matches('[data-mrw-video-section]')) return;
    const dialog = root.querySelector('[data-mrw-dialog]');
    const frame = root.querySelector('[data-mrw-dialog-frame]');
    const closeButton = root.querySelector('[data-mrw-dialog-close]');
    if (!dialog || !frame || typeof dialog.showModal !== 'function') return;
    const close = () => {
      if (dialog.open) dialog.close();
      frame.replaceChildren();
    };
    root.querySelectorAll('[data-mrw-video]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.mrwVideo;
        if (!id) return;
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
        iframe.title = button.getAttribute('aria-label') || 'MRAWARM review video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        frame.replaceChildren(iframe);
        dialog.showModal();
      }, { signal: abort.signal });
    });
    closeButton?.addEventListener('click', close, { signal: abort.signal });
    dialog.addEventListener('cancel', close, { signal: abort.signal });
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); }, { signal: abort.signal });
  }

  function initSection(root) {
    if (!root || cleanups.has(root)) return;
    const abort = new AbortController();
    const revealCleanup = initReveal(root, abort);
    initTabs(root, abort);
    initAccordion(root, abort);
    initVideo(root, abort);
    cleanups.set(root, () => { abort.abort(); revealCleanup(); });
  }

  function destroySection(root) {
    cleanups.get(root)?.();
    cleanups.delete(root);
  }

  function initAll(scope = document) {
    if (scope.matches?.('[data-mrw-section]')) initSection(scope);
    scope.querySelectorAll?.('[data-mrw-section]').forEach(initSection);
  }

  const start = () => initAll(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-mrw-section]').forEach(destroySection));
})();
